import { useState, useMemo, type Dispatch, type SetStateAction } from "react";
import type { ExpenseInput, Owner, SplitType, TransactionSplit } from "../../types";
import { money } from "../../lib/finance";
import { roundMoney } from "../../lib/money-math";
import { Users, User, Scale, HandCoins } from "lucide-react";

export interface EntrySplitFieldsProps {
  input: ExpenseInput;
  setInput: Dispatch<SetStateAction<ExpenseInput>>;
  profiles: Owner[];
  payerProfile: Owner;
  currency: string;
}

export default function EntrySplitFields({
  input,
  setInput,
  profiles,
  payerProfile,
  currency,
}: EntrySplitFieldsProps) {
  const currentSplit: TransactionSplit = input.split || { type: "joint" };
  const currentType: SplitType = currentSplit.type;
  const activeProfiles = useMemo(
    () => profiles.filter((p) => typeof p === "string" && p.trim().length > 0),
    [profiles],
  );

  const totalAmount = Number(input.amount) || 0;
  const [customShares, setCustomShares] = useState<Record<string, string>>(() => {
    const existing = currentSplit.shares || {};
    const init: Record<string, string> = {};
    for (const p of activeProfiles) {
      init[p] = existing[p] != null ? String(existing[p]) : "";
    }
    return init;
  });

  function selectType(type: SplitType) {
    if (type === "custom") {
      // Pre-fill custom shares evenly if currently blank
      const each = totalAmount > 0 ? (totalAmount / activeProfiles.length).toFixed(2) : "";
      const nextShares: Record<string, string> = {};
      const numShares: Record<string, number> = {};
      for (const p of activeProfiles) {
        nextShares[p] = customShares[p] || each;
        numShares[p] = Number(nextShares[p]) || 0;
      }
      setCustomShares(nextShares);
      setInput((v) => ({ ...v, split: { type: "custom", shares: numShares } }));
    } else {
      setInput((v) => ({ ...v, split: { type } }));
    }
  }

  function handleShareChange(profile: string, val: string) {
    const nextText = { ...customShares, [profile]: val };
    setCustomShares(nextText);
    const numShares: Record<string, number> = {};
    for (const [p, textVal] of Object.entries(nextText)) {
      const parsed = Number(textVal);
      if (Number.isFinite(parsed) && parsed >= 0) {
        numShares[p] = parsed;
      }
    }
    setInput((v) => ({ ...v, split: { type: "custom", shares: numShares } }));
  }

  const customSum = useMemo(() => {
    let sum = 0;
    for (const val of Object.values(customShares)) {
      sum += Number(val) || 0;
    }
    return roundMoney(sum);
  }, [customShares]);

  const customRemaining = roundMoney(totalAmount - customSum);

  const equalShareText = useMemo(() => {
    if (totalAmount <= 0 || activeProfiles.length <= 1) return "";
    const share = roundMoney(totalAmount / activeProfiles.length);
    return `Each person’s share: ${money(share, currency)}`;
  }, [totalAmount, activeProfiles, currency]);

  if (activeProfiles.length <= 1) return null;

  return (
    <section className="household-share entry-split-section">
      <label className="entry-split-label">Sharing & Split</label>
      <div className="entry-tabs split-tabs" role="tablist" aria-label="Sharing and split mode">
        <button
          type="button"
          role="tab"
          aria-pressed={currentType === "joint"}
          onClick={() => selectType("joint")}
        >
          <Users size={15} /> Joint
        </button>
        <button
          type="button"
          role="tab"
          aria-pressed={currentType === "equal"}
          onClick={() => selectType("equal")}
        >
          <Scale size={15} /> {activeProfiles.length === 2 ? "50/50" : "Equal"}
        </button>
        <button
          type="button"
          role="tab"
          aria-pressed={currentType === "personal"}
          onClick={() => selectType("personal")}
        >
          <User size={15} /> Personal
        </button>
        <button
          type="button"
          role="tab"
          aria-pressed={currentType === "custom"}
          onClick={() => selectType("custom")}
        >
          <HandCoins size={15} /> Custom
        </button>
      </div>

      {currentType === "joint" && (
        <small className="split-explanation">
          Full household expense. Both share it as a household; no debt is generated between you.
        </small>
      )}

      {currentType === "equal" && (
        <small className="split-explanation">
          Split evenly among {activeProfiles.join(" and ")}. {equalShareText}
        </small>
      )}

      {currentType === "personal" && (
        <small className="split-explanation">
          100% personal to {payerProfile}. Excluded from shared household totals.
        </small>
      )}

      {currentType === "custom" && (
        <div className="custom-split-container">
          <p className="custom-split-header">
            Set exact amount contributed or owed per person:
          </p>
          <div className="custom-split-grid">
            {activeProfiles.map((p) => (
              <label key={p} className="custom-split-row">
                <span>{p}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*[.]?[0-9]*"
                  placeholder="0.00"
                  value={customShares[p] ?? ""}
                  onChange={(e) => handleShareChange(p, e.target.value)}
                />
              </label>
            ))}
          </div>
          {totalAmount > 0 && Math.abs(customRemaining) > 0.005 && (
            <small className="split-remaining-notice">
              {customRemaining > 0
                ? `${money(customRemaining, currency)} left to allocate`
                : `${money(Math.abs(customRemaining), currency)} over total purchase amount`}
            </small>
          )}
        </div>
      )}
    </section>
  );
}
